/* eslint-disable prettier/prettier */
// src/modules/owner/pages/actions/tanqueCheio/components/VoucherBatchModal.jsx
import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Alert,
  Button,
  Grid,
} from '@mui/material';
import PrimaryButton from 'common/components/buttons/primaryButton';
import SecondaryButton from 'common/components/buttons/secondaryButton';
import { Close } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import axios from 'axios';
import { BASE_API, BASE_S3, USER_TOKEN } from 'config/consts';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import 'moment/locale/pt-br';
const QRCode = require('qrcode.react');

moment.locale('pt-br');

function stripHtml(html) {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

function getQrUrl(code, stationId) {
  if (process.env.REACT_APP_API_KEY === 'production') {
    return `https://app.rilato.com.br/#/qr-code-validator/voucher/${code}`;
  } else if (process.env.REACT_APP_API_KEY === 'test') {
    return `https://sandbox-gestor.rilato.com.br/#/qr-code-validator/voucher/${code}`;
  }
  return `https://localhost:3000/#/qr-code-validator/voucher/${code}`;
}

const VoucherBatchModal = ({ open, onClose, activity }) => {
  const [quantity, setQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Estado para exibir vouchers gerados
  const [generatedData, setGeneratedData] = useState(null);
  const printRef = useRef();

  const maxVouchers = 1000;

  const handlePrint = useCallback(() => {
    if (!printRef.current) return;
    // Converter canvas (QRCode) em <img> para que apareçam na impressão
    const clone = printRef.current.cloneNode(true);
    const canvases = printRef.current.querySelectorAll('canvas');
    const clonedCanvases = clone.querySelectorAll('canvas');
    canvases.forEach((canvas, i) => {
      try {
        const img = document.createElement('img');
        img.src = canvas.toDataURL('image/png');
        img.style.width = canvas.style.width || `${canvas.width}px`;
        img.style.height = canvas.style.height || `${canvas.height}px`;
        clonedCanvases[i].parentNode.replaceChild(img, clonedCanvases[i]);
      } catch (e) { /* ignore */ }
    });
    const printContents = clone.innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`
      <html>
        <head>
          <title>Vouchers</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
            .voucher-page { page-break-after: always; padding: 40px 20px; text-align: center; }
            .voucher-page:last-child { page-break-after: auto; }
            .voucher-logo { max-width: 300px; height: auto; margin-bottom: 16px; }
            .voucher-title { font-size: 1.4rem; font-weight: bold; color: #333; margin: 12px 0; }
            .voucher-date { color: #666; font-size: 0.9rem; margin: 8px 0; }
            .voucher-code { font-size: 1.3rem; font-weight: bold; color: #555; margin: 16px 0; }
            .voucher-expiry { color: #666; font-size: 0.9rem; margin-top: 16px; }
            .qr-img { display: block; margin: 16px auto; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 500);
  }, []);

  const handleGenerate = async () => {
    const qty = parseInt(quantity);

    if (!qty || qty <= 0) {
      setError('Por favor, informe uma quantidade válida');
      return;
    }

    if (qty > maxVouchers) {
      setError(`Quantidade máxima permitida: ${maxVouchers} vouchers`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.post(
        `${BASE_API}/tanque-cheio/generate-vouchers`,
        { action_id: activity?.id, quantity: qty },
        { headers: { Authorization: `${token}` } }
      );

      const data = response.data?.data;
      if (data && data.vouchers && data.vouchers.length > 0) {
        setGeneratedData(data);
        toastr.success('Sucesso', `${data.total_generated} voucher(s) gerado(s) com sucesso!`);
      } else {
        setError('Nenhum voucher foi gerado.');
      }
    } catch (err) {
      const msg = err?.response?.data?.messages?.[0] || 'Erro ao gerar vouchers. Tente novamente.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setQuantity('');
      setError('');
      setGeneratedData(null);
    }
  };

  const handleBackToForm = () => {
    setGeneratedData(null);
    setQuantity('');
  };

  // === MODO VISUALIZAÇÃO DOS VOUCHERS GERADOS ===
  if (generatedData) {
    const {
      vouchers, action_name, station_logo, voucher_expiration, rules, station_id
    } = generatedData;

    const logoSrc = station_logo ? `${BASE_S3}/${station_logo}` : null;

    return (
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Vouchers
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogActions sx={{ px: 3, pt: 0, pb: 1 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handlePrint}
            sx={{
              backgroundColor: '#7B1FA2',
              color: '#fff',
              fontWeight: 'bold',
              '&:hover': { backgroundColor: '#6A1B9A' },
            }}
          >
            IMPRIMIR
          </Button>
        </DialogActions>

        <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Box ref={printRef}>
            {vouchers.map((voucher, idx) => (
              <Box
                key={voucher.id || idx}
                className="voucher-page"
                sx={{
                  border: '1px dashed #ccc',
                  borderRadius: 2,
                  p: 4,
                  mb: 3,
                  textAlign: 'center',
                  backgroundColor: '#fff',
                  pageBreakAfter: idx < vouchers.length - 1 ? 'always' : 'auto',
                }}
              >
                {logoSrc && (
                  <Box sx={{ mb: 2 }}>
                    <img
                      className="voucher-logo"
                      src={logoSrc}
                      alt="Logo"
                      style={{ maxWidth: 300, height: 'auto' }}
                    />
                  </Box>
                )}

                <Typography className="voucher-title" variant="h5" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
                  {action_name}
                </Typography>

                <Typography className="voucher-date" variant="body2" sx={{ color: '#666', mb: 2 }}>
                  {`Voucher gerado em: ${moment().format('LLLL')}`}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <QRCode value={getQrUrl(voucher.code, station_id)} size={200} />
                </Box>

                <Typography className="voucher-code" variant="h6" sx={{ fontWeight: 'bold', color: '#555', my: 2 }}>
                  {voucher.code}
                </Typography>

                {rules && stripHtml(rules) && (
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    {stripHtml(rules)}
                  </Typography>
                )}

                <Typography className="voucher-expiry" variant="body2" sx={{ color: '#666', mt: 2 }}>
                  Este voucher deverá ser utilizado até:
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 'bold' }}>
                  {voucher_expiration ? moment(voucher_expiration).format('LLLL') : 'Sem data definida'}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SecondaryButton onClick={handleBackToForm}>
            Gerar mais
          </SecondaryButton>
          <SecondaryButton onClick={handleClose}>
            Fechar
          </SecondaryButton>
        </DialogActions>
      </Dialog>
    );
  }

  // === MODO FORMULÁRIO DE GERAÇÃO ===
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Gerador de vouchers
        <IconButton onClick={handleClose} disabled={loading}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom>
            Qual a quantidade que você quer gerar?
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Quantidade máxima {maxVouchers} vouchers
          </Typography>

          {activity && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Atividade: {activity.name}
            </Alert>
          )}

          <TextField
            fullWidth
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            placeholder="Digite a quantidade"
            inputProps={{
              min: 1,
              max: maxVouchers,
              step: 1
            }}
            disabled={loading}
            error={!!error}
            helperText={error}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <SecondaryButton onClick={handleClose} disabled={loading}>
          Cancelar
        </SecondaryButton>
        <PrimaryButton
          onClick={handleGenerate}
          disabled={loading || !quantity}
        >
          {loading ? 'Gerando...' : 'GERAR'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

VoucherBatchModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activity: PropTypes.object,
};

export default VoucherBatchModal;
